import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './InvoiceDetailsContent.styles';
import { getInvoiceDetailsRows } from './InvoiceDetailsContent.helpers';
import { Button } from '@/components/UI/Button/Button';
import { InvoiceStatusBadge } from '@/features/Profile/sections/Invoices/components/InvoiceStatusBadge';
import { Invoice } from '@/features/Profile/sections/Invoices/Invoices.types';
import { InvoiceStatus } from '@/features/Profile/sections/Invoices/api';

interface InvoiceDetailsContentProps {
  invoice: Invoice;
  onClose: () => void;
  onPay: () => void;
}

export const InvoiceDetailsContent = ({
  invoice,
  onClose,
  onPay,
}: InvoiceDetailsContentProps) => {
  const { t } = useTranslation();
  const rows = getInvoiceDetailsRows(invoice, t);
  const isUnpaid = invoice.status === InvoiceStatus.Unpaid;

  return (
    <S.Container>
      <S.Header>
        <S.Title>{invoice.service}</S.Title>
        <S.CloseButton role="button" aria-label="Close" onClick={onClose} />
      </S.Header>

      <S.StatusLine>
        <InvoiceStatusBadge status={invoice.status} />
        {!isUnpaid && (
          <S.StatusMeta $status={invoice.status}>
            : {invoice.statusDateDetails}
            {invoice.paymentSource ? `, ${invoice.paymentSource}` : ''}
          </S.StatusMeta>
        )}
      </S.StatusLine>

      <S.Details>
        {rows.map(({ label, value, isTotal, hasDividerBefore }, index) => {
          const LabelComponent = isTotal ? S.TotalLabel : S.Label;
          const ValueComponent = isTotal ? S.TotalValue : S.Value;

          return (
            <React.Fragment key={label}>
              {hasDividerBefore && index > 0 ? <S.Divider /> : null}
              <S.Row>
                <LabelComponent>{label}</LabelComponent>
                <ValueComponent>{value}</ValueComponent>
              </S.Row>
            </React.Fragment>
          );
        })}
      </S.Details>

      {invoice.canPay && (
        <Button
          appearance="secondary"
          isFullWidth
          borderRadius={14}
          onClick={onPay}
        >
          {t('common.pay')}
        </Button>
      )}
    </S.Container>
  );
};
